// Editor de formularios: pasos Tipo → Diseño → Características.
// Diseño con pestañas Bloques / Diseño / Privacidad / Analítica y vista previa escritorio/móvil.
import type { DragEvent, JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown, ArrowLeft, ArrowUp, ChevronDown, ChevronRight, EyeOff, GripVertical, Image as ImageIcon, Minus, Monitor, Plus,
  Save, ShieldCheck, Smartphone, SquareCheck, Trash2, Type,
} from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Button, Card, ErrorState, Field, Input, Loading, Select, Textarea } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { MediaPicker } from "../shared/MediaPicker.tsx";
import { FormPreview } from "./FormPreview.tsx";
import { FormSettingsPanel } from "./FormSettingsPanel.tsx";
import {
  DEFAULT_CONSENT_TEXT, DEFAULT_POLICY_URL, DEFAULT_STYLE, FONT_LABEL, KIND_LABEL, Tabs, Toggle, newId,
  type Block, type FieldOption, type FormDesign, type FormKind, type FormSettings, type FormStyle, type HiddenSource, type MktForm, type MktOptions,
  useMktOptions,
} from "./shared.tsx";

type Step = "tipo" | "diseno" | "caracteristicas";
type DesignTab = "bloques" | "diseno" | "privacidad" | "analitica";

const HIDDEN_SOURCES: Array<{ id: HiddenSource; label: string }> = [
  { id: "fixed", label: "Valor fijo" }, { id: "utm_source", label: "utm_source" }, { id: "utm_medium", label: "utm_medium" },
  { id: "utm_campaign", label: "utm_campaign" }, { id: "utm_term", label: "utm_term" }, { id: "utm_content", label: "utm_content" },
  { id: "referrer", label: "Página de referencia" }, { id: "page", label: "Página de origen" },
];

export function FormEditor(): JSX.Element {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const admin = Boolean(useMe().data?.admin);
  const opts = useMktOptions();
  const q = useQuery<{ form: MktForm }>({ queryKey: ["mkt-form", id], queryFn: () => api.get(`/mkt/forms/${id}`) });

  const [step, setStep] = useState<Step>("tipo");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<FormKind>("embedded");
  const [folder, setFolder] = useState("");
  const [design, setDesign] = useState<FormDesign>({});
  const [settings, setSettings] = useState<FormSettings>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const f = q.data?.form;
    if (!f) return;
    setName(f.name); setKind(f.kind); setFolder(f.folder ?? ""); setDesign(f.design ?? {}); setSettings(f.settings ?? {}); setDirty(false);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => api.patch<{ form: MktForm }>(`/mkt/forms/${id}`, { name: name.trim(), kind, folder: folder.trim() || null, design, settings }),
    onSuccess: (r) => { qc.setQueryData(["mkt-form", id], (old: unknown) => ({ ...(old as object), form: r.form })); void qc.invalidateQueries({ queryKey: ["mkt-forms"] }); setDirty(false); toast.success("Formulario guardado."); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });

  const upd = (d: FormDesign): void => { setDesign(d); setDirty(true); };
  const updSettings = (s: FormSettings): void => { setSettings(s); setDirty(true); };

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState message="No se pudo cargar el formulario." onRetry={() => void q.refetch()} />;

  const steps: Array<{ id: Step; label: string }> = [{ id: "tipo", label: "1. Tipo" }, { id: "diseno", label: "2. Diseño" }, { id: "caracteristicas", label: "3. Características" }];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" className="btn-ghost btn-sm" onClick={() => nav(`../${id}`)} aria-label="Volver"><ArrowLeft size={16} /></button>
          <h2 className="text-lg font-semibold text-ink truncate">{name || "Formulario"}</h2>
          {dirty && <span className="text-xs text-warning">Cambios sin guardar</span>}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { if (!dirty || window.confirm("Hay cambios sin guardar. ¿Salir de todos modos?")) nav(`../${id}`); }}>Cerrar</Button>
          {admin && <Button variant="primary" disabled={!dirty || save.isPending || !name.trim()} onClick={() => save.mutate()}><Save size={16} /> {save.isPending ? "Guardando…" : "Guardar"}</Button>}
        </div>
      </div>
      <nav className="flex gap-2 flex-wrap">
        {steps.map((s) => (
          <button key={s.id} type="button" onClick={() => setStep(s.id)}
            className={`px-3 py-1.5 rounded-full text-sm border ${step === s.id ? "bg-primary text-white border-primary" : "border-line text-ink-secondary hover:bg-surface-hover"}`}>{s.label}</button>
        ))}
      </nav>
      {!admin && <p className="text-sm text-ink-muted">Solo los administradores pueden modificar formularios.</p>}

      {step === "tipo" && (
        <Card className="p-5 space-y-4 max-w-3xl">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre" required><Input value={name} maxLength={160} onChange={(e) => { setName(e.target.value); setDirty(true); }} /></Field>
            <Field label="Carpeta" hint="Opcional, para organizar la lista.">
              <Input value={folder} maxLength={80} list="mkt-form-folders" onChange={(e) => { setFolder(e.target.value); setDirty(true); }} />
              <datalist id="mkt-form-folders">{(opts.data?.folders ?? []).map((f) => <option key={f} value={f} />)}</datalist>
            </Field>
          </div>
          <div>
            <div className="label">Tipo de formulario</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {(Object.keys(KIND_LABEL) as FormKind[]).map((k) => (
                <button key={k} type="button" onClick={() => { setKind(k); setDirty(true); }}
                  className={`card p-4 text-left ${kind === k ? "ring-2 ring-primary" : "hover:border-primary"}`}>
                  <div className="font-medium text-ink">{KIND_LABEL[k]}</div>
                  <div className="text-xs text-ink-muted mt-1">{k === "embedded" ? "Se muestra dentro de la página donde pegas el código." : k === "popup" ? "Ventana emergente según un disparador." : "Página independiente con su propia URL."}</div>
                </button>
              ))}
            </div>
          </div>
          {kind === "popup" && <PopupConfig design={design} onChange={upd} />}
          <div className="flex justify-end"><Button variant="primary" onClick={() => setStep("diseno")}>Siguiente: Diseño <ChevronRight size={16} /></Button></div>
        </Card>
      )}

      {step === "diseno" && <DesignStep design={design} onChange={upd} options={opts.data} onNext={() => setStep("caracteristicas")} />}

      {step === "caracteristicas" && (
        <Card className="p-5 max-w-3xl">
          <FormSettingsPanel value={settings} onChange={updSettings} options={opts.data} disabled={!admin} />
          <div className="flex justify-end gap-2 mt-6">
            <Button onClick={() => setStep("diseno")}>Atrás</Button>
            {admin && <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}><Save size={16} /> Guardar</Button>}
          </div>
        </Card>
      )}
    </div>
  );
}

function PopupConfig({ design, onChange }: { design: FormDesign; onChange: (d: FormDesign) => void }): JSX.Element {
  const p = design.popup ?? {};
  const set = (patch: Partial<NonNullable<FormDesign["popup"]>>): void => onChange({ ...design, popup: { ...p, ...patch } });
  return (
    <div className="grid sm:grid-cols-3 gap-4 border-t border-line pt-4">
      <Field label="Disparador">
        <Select value={p.trigger ?? "delay"} onChange={(e) => set({ trigger: e.target.value as "delay" | "exit" | "scroll" })}>
          <option value="delay">Al cargar, tras N segundos</option><option value="exit">Al intentar salir</option><option value="scroll">Al hacer scroll (%)</option>
        </Select>
      </Field>
      {(p.trigger ?? "delay") === "delay" && <Field label="Segundos"><Input type="number" min={0} max={600} value={p.delay ?? 5} onChange={(e) => set({ delay: clampInt(e.target.value, 0, 600) })} /></Field>}
      {p.trigger === "scroll" && <Field label="% de scroll"><Input type="number" min={1} max={100} value={p.scroll ?? 50} onChange={(e) => set({ scroll: clampInt(e.target.value, 1, 100) })} /></Field>}
      <div className="flex items-end pb-2"><Toggle checked={p.oncePerSession !== false} onChange={(v) => set({ oncePerSession: v })} label="Una vez por sesión" /></div>
    </div>
  );
}

const clampInt = (v: string, min: number, max: number): number => Math.min(max, Math.max(min, Math.round(Number(v) || 0)));

// ────────── Paso Diseño ──────────
function DesignStep({ design, onChange, options, onNext }: { design: FormDesign; onChange: (d: FormDesign) => void; options: MktOptions | undefined; onNext: () => void }): JSX.Element {
  const [tab, setTab] = useState<DesignTab>("bloques");
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <div className="grid lg:grid-cols-[minmax(340px,440px)_1fr] gap-4 items-start">
      <Card className="p-0 overflow-hidden">
        <div className="px-3 pt-2"><Tabs<DesignTab> value={tab} onChange={setTab} tabs={[{ id: "bloques", label: "Bloques" }, { id: "diseno", label: "Diseño" }, { id: "privacidad", label: "Privacidad" }, { id: "analitica", label: "Analítica" }]} /></div>
        <div className="p-4 max-h-[75vh] overflow-y-auto scrollbar-thin">
          {tab === "bloques" && <BlocksTab design={design} onChange={onChange} options={options} selected={selected} setSelected={setSelected} />}
          {tab === "diseno" && <StyleTab design={design} onChange={onChange} />}
          {tab === "privacidad" && <PrivacyTab design={design} onChange={onChange} />}
          {tab === "analitica" && <AnalyticsTab design={design} onChange={onChange} />}
        </div>
        <div className="flex justify-end px-4 py-3 border-t border-line"><Button variant="primary" onClick={onNext}>Siguiente: Características <ChevronRight size={16} /></Button></div>
      </Card>
      <div className="space-y-2 lg:sticky lg:top-4">
        <div className="flex justify-between items-center">
          <span className="dt-label">Vista previa</span>
          <div className="flex gap-1">
            <button type="button" className={`btn-sm ${device === "desktop" ? "btn-secondary" : "btn-ghost"}`} onClick={() => setDevice("desktop")} title="Escritorio"><Monitor size={15} /></button>
            <button type="button" className={`btn-sm ${device === "mobile" ? "btn-secondary" : "btn-ghost"}`} onClick={() => setDevice("mobile")} title="Móvil"><Smartphone size={15} /></button>
          </div>
        </div>
        <FormPreview design={design} options={options} device={device} selectedId={selected} onSelect={(bid) => { setSelected(bid); setTab("bloques"); }} />
      </div>
    </div>
  );
}

const GROUPS: Array<{ id: FieldOption["group"]; label: string }> = [
  { id: "contact", label: "Contacto" }, { id: "account", label: "Empresa" }, { id: "custom", label: "Campos personalizados (contacto)" }, { id: "accountCustom", label: "Campos personalizados (empresa)" },
];

function BlocksTab({ design, onChange, options, selected, setSelected }: {
  design: FormDesign; onChange: (d: FormDesign) => void; options: MktOptions | undefined; selected: string | null; setSelected: (id: string | null) => void;
}): JSX.Element {
  const blocks = design.blocks ?? [];
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const used = useMemo(() => new Set(blocks.filter((b) => b.kind === "field").map((b) => b.field)), [blocks]);
  const setBlocks = (bs: Block[]): void => onChange({ ...design, blocks: bs });
  const add = (b: Omit<Block, "id">): void => { const nb = { ...b, id: newId() } as Block; setBlocks([...blocks, nb]); setSelected(nb.id); setAdding(false); };
  const move = (i: number, d: number): void => { const j = i + d; if (j < 0 || j >= blocks.length) return; const bs = [...blocks]; [bs[i], bs[j]] = [bs[j]!, bs[i]!]; setBlocks(bs); };
  const patch = (bid: string, p: Partial<Block>): void => setBlocks(blocks.map((b) => (b.id === bid ? { ...b, ...p } : b)));
  const remove = (bid: string): void => setBlocks(blocks.filter((b) => b.id !== bid));
  const onDrop = (e: DragEvent, target: string): void => {
    e.preventDefault();
    if (!dragId || dragId === target) return;
    const from = blocks.findIndex((b) => b.id === dragId); const to = blocks.findIndex((b) => b.id === target);
    const bs = [...blocks]; const [m] = bs.splice(from, 1); bs.splice(to, 0, m!); setBlocks(bs); setDragId(null);
  };
  const fieldLabel = (key?: string): string => options?.fields.find((f) => f.key === key)?.label ?? key ?? "";

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        {!blocks.length && <p className="text-sm text-ink-muted">Sin bloques. Añade campos con el botón de abajo.</p>}
        {blocks.map((b, i) => {
          const open = selected === b.id;
          const title = b.kind === "field" ? (b.label || fieldLabel(b.field)) : b.kind === "text" ? `Texto: ${(b.text ?? "").slice(0, 30) || "…"}` : b.kind === "image" ? "Imagen" : b.kind === "consent" ? `Casilla: ${(b.label ?? "").slice(0, 30)}` : b.kind === "hidden" ? `Oculto: ${b.name ?? ""}` : "Separador";
          const Icon = b.kind === "text" ? Type : b.kind === "image" ? ImageIcon : b.kind === "consent" ? SquareCheck : b.kind === "hidden" ? EyeOff : b.kind === "separator" ? Minus : null;
          return (
            <div key={b.id} draggable onDragStart={() => setDragId(b.id)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, b.id)}
              className={`border rounded-md ${open ? "border-primary" : "border-line"} ${dragId === b.id ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-1 px-2 py-1.5">
                <GripVertical size={14} className="text-ink-muted cursor-grab shrink-0" />
                <button type="button" className="flex-1 min-w-0 flex items-center gap-1.5 text-left text-sm text-ink" onClick={() => setSelected(open ? null : b.id)}>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  {Icon && <Icon size={14} className="text-ink-muted shrink-0" />}
                  <span className="truncate">{title}</span>
                  {b.required && <span className="text-danger">*</span>}
                  {b.kind === "field" && b.width === "half" && <span className="text-[10px] text-ink-muted ml-1">50%</span>}
                </button>
                <button type="button" className="btn-ghost btn-sm px-1" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Subir"><ArrowUp size={13} /></button>
                <button type="button" className="btn-ghost btn-sm px-1" onClick={() => move(i, 1)} disabled={i === blocks.length - 1} aria-label="Bajar"><ArrowDown size={13} /></button>
                <button type="button" className="btn-ghost btn-sm px-1 text-danger" onClick={() => remove(b.id)} aria-label="Eliminar"><Trash2 size={13} /></button>
              </div>
              {open && <div className="px-3 pb-3 pt-1 border-t border-line"><BlockProps b={b} patch={(p) => patch(b.id, p)} options={options} /></div>}
            </div>
          );
        })}
      </div>

      {!adding ? <Button small onClick={() => setAdding(true)}><Plus size={14} /> Añadir bloque</Button> : (
        <div className="border border-line rounded-md p-3 space-y-3">
          <div className="flex justify-between items-center"><span className="dt-label">Añadir</span><button type="button" className="btn-ghost btn-sm" onClick={() => setAdding(false)}>Cerrar</button></div>
          {GROUPS.map((g) => {
            const fs = (options?.fields ?? []).filter((f) => f.group === g.id);
            if (!fs.length) return null;
            return (
              <div key={g.id}>
                <div className="text-xs font-medium text-ink-secondary mb-1">{g.label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {fs.map((f) => (
                    <button key={f.key} type="button" disabled={used.has(f.key)}
                      onClick={() => add({ kind: "field", field: f.key, label: f.label, required: f.key === "email", width: "full", ...(f.type === "tel" ? { defaultCountry: "CO" } : {}) })}
                      className="badge bg-surface-hover text-ink-secondary hover:bg-primary-soft hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed">{f.label}</button>
                  ))}
                </div>
              </div>
            );
          })}
          <div>
            <div className="text-xs font-medium text-ink-secondary mb-1">Elementos</div>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className="badge bg-surface-hover text-ink-secondary hover:bg-primary-soft hover:text-primary" onClick={() => add({ kind: "text", text: "Texto", size: "md", align: "left" })}>Texto</button>
              <button type="button" className="badge bg-surface-hover text-ink-secondary hover:bg-primary-soft hover:text-primary" onClick={() => add({ kind: "image", url: "", align: "center" })}>Imagen</button>
              <button type="button" className="badge bg-surface-hover text-ink-secondary hover:bg-primary-soft hover:text-primary" onClick={() => add({ kind: "consent", label: "Acepto recibir información comercial", required: false })}>Casilla de aceptación</button>
              <button type="button" className="badge bg-surface-hover text-ink-secondary hover:bg-primary-soft hover:text-primary" onClick={() => add({ kind: "hidden", name: `campo_${blocks.length + 1}`, source: "fixed", value: "" })}>Campo oculto</button>
              <button type="button" className="badge bg-surface-hover text-ink-secondary hover:bg-primary-soft hover:text-primary" onClick={() => add({ kind: "separator" })}>Separador</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BlockProps({ b, patch, options }: { b: Block; patch: (p: Partial<Block>) => void; options: MktOptions | undefined }): JSX.Element {
  const [picker, setPicker] = useState(false);
  if (b.kind === "field") {
    const f = options?.fields.find((x) => x.key === b.field);
    return (
      <div className="space-y-2.5">
        <div className="text-xs text-ink-muted">Campo: {f?.label ?? b.field} · {f?.type ?? "?"}</div>
        <Field label="Etiqueta"><Input value={b.label ?? ""} maxLength={300} onChange={(e) => patch({ label: e.target.value })} /></Field>
        {f?.type !== "checkbox" && <Field label="Texto de ayuda (placeholder)"><Input value={b.placeholder ?? ""} maxLength={120} onChange={(e) => patch({ placeholder: e.target.value })} /></Field>}
        {f?.type === "tel" && (
          <Field label="País por defecto">
            <Select value={b.defaultCountry ?? "CO"} onChange={(e) => patch({ defaultCountry: e.target.value })}>
              {(options?.countries ?? []).filter((c) => c.iso !== "XX").map((c) => <option key={c.iso} value={c.iso}>{c.name} ({c.dial})</option>)}
            </Select>
          </Field>
        )}
        <div className="flex flex-wrap gap-4 items-center">
          <Toggle checked={!!b.required} onChange={(v) => patch({ required: v })} label="Obligatorio" />
          <Select className="w-auto" value={b.width ?? "full"} onChange={(e) => patch({ width: e.target.value as "full" | "half" })}>
            <option value="full">Ancho 100%</option><option value="half">Ancho 50%</option>
          </Select>
        </div>
      </div>
    );
  }
  if (b.kind === "text") return (
    <div className="space-y-2.5">
      <Field label="Texto"><Textarea rows={3} maxLength={3000} value={b.text ?? ""} onChange={(e) => patch({ text: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Select value={b.size ?? "md"} onChange={(e) => patch({ size: e.target.value as "sm" | "md" | "lg" })}><option value="sm">Pequeño</option><option value="md">Normal</option><option value="lg">Destacado</option></Select>
        <Select value={b.align ?? "left"} onChange={(e) => patch({ align: e.target.value as "left" | "center" | "right" })}><option value="left">Izquierda</option><option value="center">Centro</option><option value="right">Derecha</option></Select>
      </div>
    </div>
  );
  if (b.kind === "image") return (
    <div className="space-y-2.5">
      <Field label="URL de la imagen" hint="https://… o elige de la biblioteca.">
        <div className="flex gap-2"><Input value={b.url ?? ""} maxLength={500} onChange={(e) => patch({ url: e.target.value.trim() })} /><Button small onClick={() => setPicker(true)}>Biblioteca</Button></div>
      </Field>
      <Field label="Texto alternativo"><Input value={b.alt ?? ""} maxLength={200} onChange={(e) => patch({ alt: e.target.value })} /></Field>
      <Select value={b.align ?? "center"} onChange={(e) => patch({ align: e.target.value as "left" | "center" | "right" })}><option value="left">Izquierda</option><option value="center">Centro</option><option value="right">Derecha</option></Select>
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(m) => { patch({ url: m.url }); setPicker(false); }} />
    </div>
  );
  if (b.kind === "consent") return (
    <div className="space-y-2.5">
      <Field label="Texto de la casilla"><Textarea rows={2} maxLength={300} value={b.label ?? ""} onChange={(e) => patch({ label: e.target.value })} /></Field>
      <Field label="Enlace (opcional)"><Input value={b.link ?? ""} maxLength={500} placeholder="https://…" onChange={(e) => patch({ link: e.target.value.trim() })} /></Field>
      <Toggle checked={!!b.required} onChange={(v) => patch({ required: v })} label="Obligatoria" />
    </div>
  );
  if (b.kind === "hidden") return (
    <div className="space-y-2.5">
      <Field label="Nombre interno" hint="Letras, números y _ (aparece en los envíos y en el CSV)."><Input value={b.name ?? ""} maxLength={40} onChange={(e) => patch({ name: e.target.value.replace(/[^A-Za-z0-9_]/g, "") })} /></Field>
      <Field label="Valor">
        <Select value={b.source ?? "fixed"} onChange={(e) => patch({ source: e.target.value as HiddenSource })}>{HIDDEN_SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</Select>
      </Field>
      {(b.source ?? "fixed") === "fixed" && <Input value={b.value ?? ""} maxLength={200} placeholder="p. ej. Seminario SST 2026" onChange={(e) => patch({ value: e.target.value })} />}
    </div>
  );
  return <p className="text-xs text-ink-muted">Línea separadora, sin opciones.</p>;
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <Field label={label}>
      <div className="flex gap-2 items-center">
        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-10 rounded border border-line bg-transparent p-0.5" />
        <Input value={value} maxLength={9} onChange={(e) => onChange(e.target.value.trim())} className="font-mono text-xs" />
      </div>
    </Field>
  );
}

function StyleTab({ design, onChange }: { design: FormDesign; onChange: (d: FormDesign) => void }): JSX.Element {
  const st = { ...DEFAULT_STYLE, ...(design.style ?? {}) };
  const set = (p: Partial<FormStyle>): void => onChange({ ...design, style: { ...st, ...p } });
  const [picker, setPicker] = useState(false);
  return (
    <div className="space-y-3">
      <Field label="Título"><Input value={design.title ?? ""} maxLength={200} onChange={(e) => onChange({ ...design, title: e.target.value })} /></Field>
      <Field label="Subtítulo"><Textarea rows={2} maxLength={1000} value={design.subtitle ?? ""} onChange={(e) => onChange({ ...design, subtitle: e.target.value })} /></Field>
      <Field label="Texto del botón"><Input value={design.buttonLabel ?? ""} maxLength={60} placeholder="Enviar" onChange={(e) => onChange({ ...design, buttonLabel: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <ColorInput label="Fondo" value={st.bg} onChange={(v) => set({ bg: v })} />
        <ColorInput label="Texto" value={st.text} onChange={(v) => set({ text: v })} />
        <ColorInput label="Fondo de campos" value={st.fieldBg} onChange={(v) => set({ fieldBg: v })} />
        <ColorInput label="Texto de campos" value={st.fieldText} onChange={(v) => set({ fieldText: v })} />
        <ColorInput label="Borde de campos" value={st.fieldBorder} onChange={(v) => set({ fieldBorder: v })} />
        <ColorInput label="Botón" value={st.button} onChange={(v) => set({ button: v })} />
        <ColorInput label="Texto del botón" value={st.buttonText} onChange={(v) => set({ buttonText: v })} />
        <Field label="Fuente"><Select value={st.font} onChange={(e) => set({ font: e.target.value })}>{Object.entries(FONT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select></Field>
        <Field label={`Radio de bordes (${st.radius}px)`}><input type="range" min={0} max={30} value={st.radius} onChange={(e) => set({ radius: Number(e.target.value) })} className="w-full" /></Field>
        <Field label={`Relleno (${st.padding}px)`}><input type="range" min={0} max={80} value={st.padding} onChange={(e) => set({ padding: Number(e.target.value) })} className="w-full" /></Field>
        <Field label="Ancho máximo (px)"><Input type="number" min={280} max={1200} value={st.maxWidth} onChange={(e) => set({ maxWidth: clampInt(e.target.value, 280, 1200) })} /></Field>
      </div>
      <Field label="Imagen de fondo" hint="Opcional. Se oscurece con la capa (overlay) para que el texto se lea.">
        <div className="flex gap-2"><Input value={st.bgImage} maxLength={500} placeholder="https://…" onChange={(e) => set({ bgImage: e.target.value.trim() })} /><Button small onClick={() => setPicker(true)}>Biblioteca</Button></div>
      </Field>
      {st.bgImage && (
        <div className="grid grid-cols-2 gap-3">
          <ColorInput label="Color de la capa" value={st.overlayColor} onChange={(v) => set({ overlayColor: v })} />
          <Field label={`Opacidad de la capa (${Math.round(st.overlay * 100)}%)`}><input type="range" min={0} max={95} value={Math.round(st.overlay * 100)} onChange={(e) => set({ overlay: Number(e.target.value) / 100 })} className="w-full" /></Field>
        </div>
      )}
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(m) => { set({ bgImage: m.url }); setPicker(false); }} />
    </div>
  );
}

function PrivacyTab({ design, onChange }: { design: FormDesign; onChange: (d: FormDesign) => void }): JSX.Element {
  const p = design.privacy ?? {};
  const set = (patch: Partial<NonNullable<FormDesign["privacy"]>>): void => onChange({ ...design, privacy: { ...p, ...patch } });
  return (
    <div className="space-y-4">
      <div className="flex gap-2 text-xs text-ink-muted"><ShieldCheck size={16} className="shrink-0 text-primary" /> Ley 1581 de 2012 (Colombia) y Ley 29733 (Perú): el consentimiento expreso queda guardado con fecha y texto en cada envío.</div>
      <Toggle checked={p.consent !== false} onChange={(v) => set({ consent: v })} label="Casilla de consentimiento obligatoria" />
      {p.consent !== false && (
        <>
          <Field label="Texto del consentimiento"><Textarea rows={3} maxLength={600} value={p.text ?? DEFAULT_CONSENT_TEXT} onChange={(e) => set({ text: e.target.value })} /></Field>
          <Field label="Enlace a la política de privacidad"><Input value={p.policyUrl ?? DEFAULT_POLICY_URL} maxLength={500} onChange={(e) => set({ policyUrl: e.target.value.trim() })} /></Field>
        </>
      )}
      <Toggle checked={!!p.doubleOptIn} onChange={(v) => set({ doubleOptIn: v })} label="Doble opt-in"
        hint="Se envía un correo con un enlace de confirmación. Las listas, la respuesta automática y las automatizaciones esperan a que el contacto confirme." />
      {p.doubleOptIn && <Field label="Asunto del correo de confirmación"><Input value={p.confirmSubject ?? ""} maxLength={200} placeholder="Confirma tu suscripción" onChange={(e) => set({ confirmSubject: e.target.value })} /></Field>}
    </div>
  );
}

function AnalyticsTab({ design, onChange }: { design: FormDesign; onChange: (d: FormDesign) => void }): JSX.Element {
  return (
    <div className="space-y-3">
      <Toggle checked={design.analytics?.utm !== false} onChange={(v) => onChange({ ...design, analytics: { ...(design.analytics ?? {}), utm: v } })}
        label="Capturar UTM automáticamente" hint="utm_source, utm_medium, utm_campaign, utm_term y utm_content de la URL, la página de referencia y la página de origen. Se guardan en cada envío." />
      <p className="text-xs text-ink-muted">Para guardar un valor concreto (p. ej. el nombre de un evento), añade un <strong>campo oculto</strong> en la pestaña Bloques con valor fijo o tomado de un parámetro UTM.</p>
      <p className="text-xs text-ink-muted">Las impresiones se cuentan cada vez que el formulario se muestra (incrustado, página o pop-up) y las conversiones en cada envío válido.</p>
    </div>
  );
}
